/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { APIError, APIResponse } from '@/core/types/utils'
import {
  LoginFormValues,
  LoginResponse,
  User,
  UserFormValues,
  UserRole,
  UserUpdateFormValues,
} from './auth.types'
import fetcher from '@/core/lib/fetcher'
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from '../ui/use-toast'

export const login = (credentials: LoginFormValues) => {
  return fetcher
    .url(import.meta.env.VITE_API_BOOKING + 'auth/signin')
    .post(credentials)
    .json<APIResponse<LoginResponse>>()
}

export const checkUserName = (username: string) => {
  return fetcher
    .get(
      import.meta.env.VITE_API_BOOKING + `users/check-username/${username}`
    )
    .error(409, () => {
      return { message: 'Username already exists', data: true, success: false }
    })
    .json<APIResponse<APIResponse<null | boolean>>>()
    .catch(() => {
      return {
        message: 'Unknown error validating username',
        data: true,
        success: false,
      }
    })
}

export const usersQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ['users', token],
    queryFn: () => {
      return fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + 'user')
        .json<APIResponse<User[]>>()
    },
  })

export const createUser = (user: UserFormValues, token: string) => {
  return fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + 'user')
    .post(user)
    .json<APIResponse<User>>()
}

export const useDeleteUser = (token: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(import.meta.env.VITE_API_BOOKING + `user/${id}`)
        .delete()
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['users'],
      })
    },
    onError: (error) => {
      const errorObject: APIError<null> = JSON.parse(error.message)
      const errorMessage = errorObject.message
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

export const userByIdQueryOptions = (id: string, token: string) =>
  queryOptions({
    queryKey: ['user', id, token],
    queryFn: () => {
      return fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + `user/${id}`)
        .json<APIResponse<User>>()
    },
  })

export const updateUser = (
  user: UserFormValues,
  userId: string,
  token: string
) => {
  return fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `user/${userId}`)
    .put(user)
    .json<APIResponse<User>>()
}

export const updateUserProfile = (data: UserUpdateFormValues, token: string) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `user/me`)
    .put(data)
    .json<APIResponse<User>>()

export const resetPassword = (data: { token: string; newPassword: string }) =>
  fetcher
    .url(import.meta.env.VITE_API_BOOKING + 'auth/reset-password')
    .post(data)
    .json<APIResponse<APIResponse<[]>>>()

export const forgotPassword = (username: string, userId?: string) =>
  fetcher
    .url(import.meta.env.VITE_API_BOOKING + 'auth/forgot-password')
    .post({ username, userId })
    .json<
      APIResponse<
        APIResponse<{
          userId: string
          signInId: string
          email: string
          username: string
        }>
      >
    >()

export const getRoles = (token: string) =>
  queryOptions({
    queryKey: ['roles', token],
    queryFn: () => {
      return fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + 'user/roles')
        .json<APIResponse<UserRole[]>>()
    },
  })
